// Trieda na spravu dialogov v aplikacii
class DialogManager {
    // Konstruktor inicializuje potrebne premenne a defaultne nastavenia
    constructor() {
        this.dialogElements = {};
        this.dialogOptions = {};
        this.eventListeners = {};
        this.openOrder = []; // pole sledujuce poradie otvorenych dialogov
        this.defaultOptions = {
            id: 'universal-dialog', // defaultne id dialogu
            cssClass: '', // dodatočné css triedy pre dialog element (oddelené medzerou)
            content: '', // obsah dialogu ako HTML string
            modal: true, // ci je dialog modalny (blokuje pozadie ::backdrop)
            fullScreen: true, // ci je dialog na celu obrazovku
            url: false, // URL na nacitanie obsahu dialogu;
            persistent: false, // ci sa dialog ne/ma odstranit po zatvoreni z html. Ak true, tak sa len skryje (napr. hlavne menu, ktore je zapisane v index.html)
            autoClose: null, // čas v ms, po ktorom sa dialog automaticky zavrie (null = nezatvara sa automaticky) - pripravene pre nemodal hlasky
            jsEval: true, // ci sa maju pred otvorenim spustit vsetky skripty v obsahu dialogu
            parseJQM: true, // ci sa ma pred otvorenim pre obsah spustit parsovanie jquery mobile komponentov v dialogu
            backdropDismiss: true, // ci sa ma dialog zatvorit pri kliknuti na pozadie (backdrop) - len pre modalne dialogy
            backdropDismissOverride: null, // funkcia (ako string), ktora sa zavola pri kliknuti na pozadie (backdrop) - len pro modalne dialogy; ak je zadana, tak prepise defaultne spravanie backdropDismiss (t.j. zatvorenie dialogu)
            clickOutsideDismiss: false, // ci sa ma dialog zatvorit pri kliknuti mimo dialogu (ine elementy, vratane pozadia) - len pro nemodalne dialogy
            onOpen: null, // callback funkcia pred otvorenim dialogu - pred parsovanim a evaluaciou
            onShow: null, // callback funkcia pred zobrazenim dialogu
            shown: null, // callback funkcia po zobrazeni dialogu
            onClose: null, // callback funkcia pred zatvorenim dialogu
            closed: null, // callback funkcia po zatvoreni dialogu
            onError: null, // callback funkcia pri chybe
            waitForEvent: null, // nazov eventu, na ktory sa ma cakat pred otvorenim dialogu (ak je zadany)
            parentElement: null // element, do ktoreho sa ma dialog vlozit; ak null, vlozi sa do body
        };
    }

    // Otvori dialog s danymi volbami
    async open(options = {}) {
        const opts = Object.assign({}, this.defaultOptions, options);
        this.dialogOptions[opts.id] = opts;
        try {
            // Volanie onOpen callback funkcie
            if (typeof opts.onOpen === 'function') opts.onOpen();
            // Vytvorime dialogovy element, ak este neexistuje a ak ano, tak ho aktualizujeme
            await this.create(opts.id);
            if (!this.dialogElements[opts.id]) return;
            // Ulozime options priamo do DOM elementu pre neskorsie pouzitie
            this.dialogElements[opts.id]._dialogOptions = opts;
            // Ak je zadany waitForEvent, pockame na jeho spustenie
            if (opts.waitForEvent && typeof opts.waitForEvent === 'string') {
                await new Promise((resolve) => {
                    const eventHandler = () => {
                        window.removeEventListener(opts.waitForEvent, eventHandler);
                        resolve();
                    };
                    window.addEventListener(opts.waitForEvent, eventHandler);
                });
            }
            // Spustime evaluaciu skriptov a parsovanie JQM komponentov, ak je to zadane
            if (opts.jsEval) this.jsEval(opts.id);
            if (opts.parseJQM) this.parseJQM(opts.id);
            // Zobrazime dialog
            this.show(opts.id);

            // Automatické zatvorenie dialógu po uplynutí autoClose
            if (opts.autoClose && typeof opts.autoClose === 'number' && opts.autoClose > 0) {
                // Zruš predchádzajúci timeout ak existuje
                if (this.dialogElements[opts.id]._autoCloseTimer) {
                    clearTimeout(this.dialogElements[opts.id]._autoCloseTimer);
                }
                this.dialogElements[opts.id]._autoCloseTimer = setTimeout(() => {
                    this.close(opts.id);
                }, opts.autoClose);
            }
        } catch (err) {
            console.error('DialogManager.open error:', err);
            if (typeof opts.onError === 'function') opts.onError(err);
        }
    }

    // Vytvori dialogovy element, ak este neexistuje
    async create(id) {
        const opts = this.dialogOptions[id] || this.defaultOptions;
        try {
            let dialogEl = document.getElementById(id);
            // Ak element s danym id existuje, ale nie je to dialog, vypiseme varovanie a vytvorime novy dialog
            if (dialogEl && dialogEl.tagName !== 'DIALOG') {
                console.warn('Duplicate element with id: "' + id + '"');
                dialogEl = null;
            }
            // Ak dialog este neexistuje, vytvorime novy
            if (!dialogEl) {
                dialogEl = document.createElement('dialog');
                dialogEl.id = id;
                dialogEl.classList.add('dialog');
                if (opts.fullScreen) dialogEl.classList.add('dialog--full-screen');
                if (opts.cssClass) dialogEl.classList.add(...opts.cssClass.trim().split(/\s+/));
                if (opts.backdropDismiss) {
                    if (opts.backdropDismissOverride != null) {
                        dialogEl.setAttribute('onmousedown', `if (event.target === this) { ${opts.backdropDismissOverride} }`);
                    } else {
                        dialogEl.setAttribute('onmousedown', `if (event.target === this) { dialog.close('${id}'); }`);
                    }
                } else {
                    dialogEl.removeAttribute('onmousedown');
                }
                dialogEl._dialogOptions = opts;
                this.dialogElements[id] = dialogEl;
            } else {
                // Ak uz existuje, aktualizujeme options a vlastnosti elementu
                dialogEl._dialogOptions = opts;
                // Aktualizujeme CSS triedy
                dialogEl.classList.add('dialog'); // pridamame zakladnu triedu
                if (opts.fullScreen) dialogEl.classList.add('dialog--full-screen');
                if (opts.cssClass) dialogEl.classList.add(...opts.cssClass.trim().split(/\s+/));
                // Aktualizujeme backdrop dismiss handler
                if (opts.backdropDismiss) {
                    if (opts.backdropDismissOverride != null) {
                        dialogEl.setAttribute('onmousedown', `if (event.target === this) { ${opts.backdropDismissOverride} }`);
                    } else {
                        dialogEl.setAttribute('onmousedown', `if (event.target === this) { dialog.close('${id}'); }`);
                    }
                } else {
                    dialogEl.removeAttribute('onmousedown');
                }
                this.dialogElements[id] = dialogEl;
            }
            // Naplnime obsah dialogu
            await this.fillContent(dialogEl.id);
            if (!document.body.contains(this.dialogElements[id])) {
                // Ak je zadany parentElement, vlozime dialog do neho, inak do body
                // tooltip ktory sa otvara ako modal: false nie je top-layer a tym padom nie je nad modal dialogom 
                if (opts.parentElement) {
                    opts.parentElement.appendChild(this.dialogElements[id]);
                } else {
                    document.body.appendChild(this.dialogElements[id]);
                }
            }
        } catch (err) {
            console.error('DialogManager.create error:', err);
            if (typeof opts.onError === 'function') opts.onError(err);
        }
    }

    // Nacita obsah z url pre dialog
    async load(urlString) {
        let urlToLoad;
        try {
            urlToLoad = new URL(urlString, url.base);
        } catch (err) {
            return '';
        }
        try {
            const response = await fetch(urlToLoad);
            return await response.text();
        } catch (e) {
            console.error('Dialog load error:', e);
            return '';
        }
    }

    // Naplni obsah dialogu podla zadanych dat alebo url
    async fillContent(id, content = '') {
        var dialogEl = this.dialogElements[id];
        const opts = this.dialogOptions[id] || this.defaultOptions;
        try {
            if (!dialogEl) return;
            // Ak nie je obsah priamo zadany, skusime ho ziskat z options
            if (!content && opts.content) content = opts.content;
            // plnim obsah z url, ak nie je zadany priamo
            if (!content && opts.url) content = await this.load(opts.url);
            if (!content) return;
            dialogEl.innerHTML = content;
            this.dialogElements[id] = dialogEl;
        } catch (err) {
            console.error('DialogManager.fillContent error:', err);
            if (typeof opts.onError === 'function') opts.onError(err);
        }
    }

    // Zobrazi dialog na obrazovke
    show(id) {
        var dialogEl = this.dialogElements[id];
        const opts = this.dialogOptions[id] || this.defaultOptions;
        try {
            if (dialogEl && !dialogEl.open && dialogEl.innerHTML.trim() !== '') {
                dialogEl.classList.add('dialog--opening')
                if (typeof opts.onShow === 'function') opts.onShow();
                if (opts.modal) { 
                    dialogEl.showModal();
                } else {
                    dialogEl.show();
                    // Pre nemodalne dialogy - zatvorenie pri kliknuti mimo dialóg
                    if (opts.clickOutsideDismiss) {
                        this._handleClickOutside(id);
                    }
                }
                // Pridaj dialog do openOrder pre sledovanie poradia
                if (!this.openOrder.includes(id)) {
                    this.openOrder.push(id);
                }
                if (typeof opts.shown === 'function') opts.shown();
                dialogEl.classList.add('dialog--open');
                dialogEl.classList.remove('dialog--opening');
            } else {
                console.warn('Dialog cannot be shown. It may already be open or has no content.');
            }
        } catch (err) {
            console.error('DialogManager.show error:', err);
            if (typeof opts.onError === 'function') opts.onError(err);
        }
    }

    // Pomocná funkcia na spracovanie kliknutia mimo dialogu pre nemodalne dialogy s clickOutsideDismiss
    _handleClickOutside(id) {
        const dialogEl = this.dialogElements[id];
        const clickOutsideHandler = (event) => {
            if (!dialogEl.contains(event.target) && dialogEl.open) {
                this.close(id);
            }
        };
        // Ulozime handler pre následné odstranenie event listenera pri zatvorení dialogu
        if (!this.eventListeners[id]) this.eventListeners[id] = {};
        this.eventListeners[id].clickOutside = clickOutsideHandler;
        // Pridame listener s malym delayom, aby sa nezachytil tento istý click ktory otvoril dialog
        setTimeout(() => {
            document.addEventListener('mousedown', clickOutsideHandler);
        }, 100);
    }

    // Pomocná funkcia na zatvorenie jedného dialógu podľa id
    _closeOne(id) {
        const closeCallback = (opts) => { if (typeof opts.onClose === 'function') opts.onClose(); };
        const closedCallback = (opts) => { if (typeof opts.closed === 'function') opts.closed(); };
        const dialogEl = this.dialogElements[id];
        const opts = (dialogEl && dialogEl._dialogOptions) ? dialogEl._dialogOptions : (this.dialogOptions[id] || this.defaultOptions);
        if (dialogEl) {
            closeCallback(opts);
            // Zruš automatický timer ak existuje
            if (dialogEl._autoCloseTimer) {
                clearTimeout(dialogEl._autoCloseTimer);
                delete dialogEl._autoCloseTimer;
            }
            // Odstran click outside listener ak existuje
            if (this.eventListeners[id] && this.eventListeners[id].clickOutside) {
                document.removeEventListener('mousedown', this.eventListeners[id].clickOutside);
            }
            // Odstran dialog z openOrder
            const orderIndex = this.openOrder.indexOf(id);
            if (orderIndex !== -1) {
                this.openOrder.splice(orderIndex, 1);
            }
            dialogEl.classList.remove('dialog--open');
            dialogEl.classList.add('dialog--closing');
            dialogEl.close(); // pri debugovani, ak je debugpoint pred close(), tak to tu akoby skonci (it will also remove the modal state from the page), ale v skutocnosti to bezi dalej, treba si dat debugpoint aj za tento riadok
            dialogEl.classList.remove('dialog--closing');
            closedCallback(opts);
            // Neodstrani elementu z DOM, ak je persistentny (napr. hlavne menu)
            if (opts.persistent) return;
            delete this.dialogElements[id];
            delete this.dialogOptions[id];
            dialogEl.remove();
            // Odstrani event listenery
            if (this.eventListeners[id]) {
                delete this.eventListeners[id];
            }
        }
    }

    // Zavrie dialog alebo viac dialogov podla id
    close(id) {
        console.warn("🐞🐞🐞🐞🐞 id: %c" + id + "%c closed by: pozri call stack!", "font-weight:bold;color:purple", "");
        try {
            if (typeof id === 'undefined') {
                for (const key in this.dialogElements) {
                    if (this.dialogElements.hasOwnProperty(key)) {
                        this.isOpen(key) && this._closeOne(key);
                    }
                }
            } else if (Array.isArray(id)) {
                for (let i = 0; i < id.length; i++) {
                    this.isOpen(id[i]) && this._closeOne(id[i]);
                }
            } else {
                this.isOpen(id) && this._closeOne(id);
            }
        } catch (err) {
            const opts = (id && this.dialogOptions[id]) ? this.dialogOptions[id] : this.defaultOptions;
            console.error('DialogManager.close error:', err);
            if (typeof opts.onError === 'function') opts.onError(err);
        }
    }

    // Zisti, ci je dialog otvoreny
    isOpen(id) {
        if (typeof id === 'undefined') {
            return Object.keys(this.dialogElements).some(key => this.dialogElements[key].open);
        }
        var dialogEl = this.dialogElements[id];
        return dialogEl ? dialogEl.open : false;
    }

    // Vrati pole id otvorenych dialogov v poradi ich otvorenia
    getOpenDialogs() {
        return this.openOrder.filter(id => this.isOpen(id));
    }

    // Vrati posledny otvoreny dialog (ten ktory je uplne navrchu)
    getCurrentTop() {
        if (this.openOrder.length === 0) return null;
        const topDialogId = this.openOrder[this.openOrder.length - 1];
        return this.dialogElements[topDialogId] || null;
    }

    // Vrati dialog podla id
    getById(id) {
        return this.dialogElements[id] || null;
    }

    // Spusti vsetky skripty v obsahu dialogu
    jsEval(id) {
        var dialogEl = this.dialogElements[id];
        if (!dialogEl) return;
        var scripts = dialogEl.querySelectorAll('script');
        scripts.forEach(function(script) {
            if (script.textContent) {
                try { eval(script.textContent); } catch (e) { console.error(e); }
            }
        });
    }

    // Inicializuje jQuery Mobile komponenty v dialogu
    parseJQM(id) {
        var dialogEl = this.dialogElements[id];
        if (!dialogEl) return;
        $(dialogEl).find("*").trigger('create');
    }
}

// Instancia triedy DialogManager pre pouzitie v aplikacii - inicializovana v structure.js
dialog = new DialogManager();